// ─── Sélecteur de PLAN (barre de puces) ──────────────────────────
// Un espace peut avoir PLUSIEURS plans (Maison, Garage, Cave…). Cette barre
// liste les plans accessibles, permet d'en choisir un (actif), d'en créer /
// renommer / supprimer (réservé à qui peut écrire). Autonome : gère sa propre
// feuille de création/édition ; l'écran parent fait les appels service.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { useUI, useT, Feuille, SelecteurEmoji, confirmerSuppression } from '@mop/core';

// `peutEditer` = j'ai le droit d'ecrire ; `peutCreer` = il me reste un jeton pour
// POSSEDER un element de plus. Deux choses distinctes : on peut renommer ses
// plans sans avoir le droit d'en creer un nouveau.
export default function SelecteurPlan({
  plans = [], planSelId, onSelect, peutEditer = true, peutCreer = true, messageQuota = '',
  onCreer, onRenommer, onSupprimer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [form, setForm] = useState(null);   // { id?, nom, emoji }

  useEffect(() => { if (!plans.length) setForm(null); }, [plans.length]);

  const ouvrirCreer = () => setForm({ id: null, nom: '', emoji: '🗺️' });
  const ouvrirEditer = (p) => setForm({ id: p.id, nom: p.nom || '', emoji: p.emoji || '🗺️', nomInit: p.nom || '', emojiInit: p.emoji || '🗺️' });
  const nomT = (form?.nom || '').trim();
  const modifie = !form?.id ? !!nomT : (nomT !== (form?.nomInit || '') || form?.emoji !== (form?.emojiInit || ''));

  function enregistrer() {
    if (!nomT || !modifie) return;
    if (form.id) onRenommer?.(form.id, { nom: nomT, emoji: form.emoji });
    else onCreer?.({ nom: nomT, emoji: form.emoji });
    setForm(null);
  }

  return (
    <>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.barre} contentContainerStyle={{ gap: 8, paddingHorizontal: 14, paddingVertical: 8 }}>
        {plans.map(p => {
          const actif = p.id === planSelId;
          return (
            <TouchableOpacity key={p.id} style={[s.chip, actif && s.chipOn]} activeOpacity={0.7}
              onPress={() => onSelect?.(p.id)}
              onLongPress={peutEditer ? () => ouvrirEditer(p) : undefined} delayLongPress={400}>
              <Text style={s.chipEmoji}>{p.emoji || '🗺️'}</Text>
              <Text style={[s.chipTxt, actif && s.chipTxtOn]} numberOfLines={1}>{p.nom || t('inventaire.plan')}</Text>
            </TouchableOpacity>
          );
        })}
        {peutEditer && peutCreer && (
          <TouchableOpacity style={s.chipAjout} onPress={ouvrirCreer} accessibilityLabel={t('plans.nouveauPlan')}>
            <Text style={s.chipAjoutTxt}>＋ {t('inventaire.plan')}</Text>
          </TouchableOpacity>
        )}
      </ScrollView>
      {plans.length > 1 && <Text style={s.aide}>{t('plans.selecteurAide')}</Text>}
      {peutEditer && !peutCreer && !!messageQuota && <Text style={s.aide}>{messageQuota}</Text>}

      {/* Feuille création / édition d'un plan. */}
      <Feuille visible={!!form} titre={form?.id ? t('plans.modifierPlan') : t('plans.nouveauPlan')} emoji={form?.emoji || '🗺️'} hauteur={0.82}
        onFermer={() => setForm(null)}
        action={{ icone: form?.id ? '💾' : '＋', onPress: enregistrer, disabled: !nomT || !modifie, label: t('commun.enregistrer') }}
        boutons={form?.id ? (
          <TouchableOpacity onPress={() => confirmerSuppression(t('plans.supprimerPlanTitre'), t('plans.supprimerPlanMsg', { nom: form.nomInit }), () => { onSupprimer?.(form.id); setForm(null); })} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityLabel={t('commun.supprimer')}>
            <Text style={s.suppr}>🗑️</Text>
          </TouchableOpacity>
        ) : null}>
        <Text style={s.lbl}>{t('plans.nomPlan')}</Text>
        <TextInput style={s.input} value={form?.nom || ''} onChangeText={(v) => setForm(f => ({ ...f, nom: v }))}
          placeholder={t('plans.nomPlanPlaceholder')} placeholderTextColor={UI.faint} />
        <Text style={s.lbl}>{t('profil.icone')}</Text>
        <SelecteurEmoji inline avecRecherche valeur={form?.emoji || '🗺️'} onChange={(e) => setForm(f => ({ ...f, emoji: e }))} />
      </Feuille>
    </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  barre: { borderBottomWidth: 0.5, borderBottomColor: UI.border, backgroundColor: UI.card, maxHeight: 52 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, backgroundColor: UI.champ, borderWidth: 1, borderColor: UI.champBord },
  chipOn: { backgroundColor: UI.accentTint, borderColor: UI.accent },
  chipEmoji: { fontSize: 15 },
  chipTxt: { fontSize: 13, color: UI.ink, fontWeight: '700', maxWidth: 130 },
  chipTxtOn: { color: UI.accent },
  chipAjout: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, borderWidth: 1, borderColor: UI.accent, borderStyle: 'dashed' },
  chipAjoutTxt: { fontSize: 13, color: UI.accent, fontWeight: '700' },
  aide: { fontSize: 11, color: UI.faint, paddingHorizontal: 14, marginTop: 2 },
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 12, marginBottom: 7 },
  input: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  suppr: { fontSize: 18 },
});
